import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import NavMenuOption from './NavMenuOption';
import NavAdminPanel from '../NavAdminPanel';
import NavFormsPanel from '../NavFormsPanel';
import NavCategoryPanel from '../NavCategoryPanel';
import NavWorkspacePanel from '../NavWorkspacePanel';
import NavSetsPanel from '../NavSetsPanel';
import NavThesaurusPanel from '../NavThesaurusPanel';
import NavReportsPanel from '../NavReportsPanel';
import { isWorkbench } from '../../utils/piction';
import appConfig from '../../appConfig';
import HomeIcon from '../Icons/Home';
import AdminIcon from '../Icons/Admin';
import DocDefault from '../Icons/DocDefault';
import AdminActiveIcon from '../Icons/AdminSelected';
import CategoryIcon from '../Icons/CategoryDefault';
import CategorySelectedIcon from '../Icons/CategorySelected';
import WorkspaceIcon from '../Icons/WorkspacesDefault';
import WorkspaceSelectedIcon from '../Icons/WorkspacesSelected';
import FormIcon from '../Icons/FormsDefault';
import FormSelectedIcon from '../Icons/FormsSelected';
import HighlightIcon from '../Icons/HighlightsDefault';
import HighlightSelected from '../Icons/HighlightsSelected';
import ShareDefaultIcon from '../Icons/ShareDefault';
import { NAV_CATEGORIES } from '../../constants/nav';

function NavOptions(props) {
  const { intl, selectedComponent } = props;
  const navPanelComponent = useSelector((state) =>
    state.mainUI.get('navPanelComponent')
  );
  const externalNavLinks = appConfig.navExternalLink ? [] : null;
  if (Array.isArray(appConfig.navExternalLink)) {
    appConfig.navExternalLink.forEach((navLink) => {
      if (navLink.title && navLink.url) {
        externalNavLinks.push(
          <li
            key={`nav_link-${navLink.title.toLowerCase().replace(' ', '_')}`}
            className={classNames(
              'nav-options__link',
              `nav_link-${navLink.title.toLowerCase().replace(' ', '_')}`
            )}
            onClick={() => props.onExternalLinkClick(appConfig.navExternalLink)}
          >
            <NavMenuOption navLabel={navLink.title}>
              <i
                className="nav-options__icon nav-options__icon--info"
                aria-hidden="true"
              />
            </NavMenuOption>
          </li>
        );
      }
    });
  } else if (
    appConfig.navExternalLink &&
    appConfig.navExternalLink.title &&
    appConfig.navExternalLink.url
  ) {
    externalNavLinks.push(
      <li
        key="nav_link-external"
        className={classNames('nav-options__link')}
        onClick={() => props.onExternalLinkClick(appConfig.navExternalLink)}
      >
        <NavMenuOption navLabel={appConfig.navExternalLink.title}>
          <i
            className="nav-options__icon nav-options__icon--info"
            aria-hidden="true"
          />
        </NavMenuOption>
      </li>
    );
  }

  // This effect is responsible for automatically switching to the NavCategoryPanel
  // When there is a change in navPanelComponent
  useEffect(() => {
    const selectedType = selectedComponent && selectedComponent.type;
    if (
      navPanelComponent === NAV_CATEGORIES &&
      selectedType !== (<NavCategoryPanel />).type
    ) {
      props.onClick(<NavCategoryPanel />, false /*deselectUmos*/);
    }
  }, [navPanelComponent, selectedComponent, props]);

  return (
    <ul className="nav-options">
      {appConfig.removeHomeButton ? null : (
        <li
          className={classNames('nav-options__link')}
          onClick={props.onHomeClick}
          key="nav-home"
        >
          <NavMenuOption
            navLabel={intl.formatMessage({ id: 'navOptions.homeHeader' })}
          >
            <div>
              <HomeIcon className="nav-options__icon" />
            </div>
          </NavMenuOption>
        </li>
      )}
      {isWorkbench() &&
      appConfig.menuOptions.adminMenu &&
      appConfig.menuOptions.adminMenu.length > 0 ? (
        <li
          key="nav_link-admin"
          className={classNames('nav-options__link', {
            'nav-options__link--selected':
              selectedComponent &&
              selectedComponent.type === (<NavAdminPanel />).type,
          })}
          onClick={() => props.onClick(<NavAdminPanel />)}
        >
          <NavMenuOption
            navLabel={intl.formatMessage({ id: 'navOptions.adminHeader' })}
          >
            <div>
              <div>
                <AdminIcon className="nav-options__icon" />
              </div>
            </div>
          </NavMenuOption>
        </li>
      ) : null}
      {appConfig.removeCategories ? null : (
      <li
        key="nav_link-categories"
        className={classNames('nav-options__link', {
          'nav-options__link--selected':
            selectedComponent &&
            selectedComponent.type === (<NavCategoryPanel />).type,
        })}
        onClick={() => props.onClick(<NavCategoryPanel />)}
      >
        <NavMenuOption
          navLabel={intl.formatMessage({ id: 'navOptions.categoryHeader' })}
        >
          <div>
            <div>
              <CategoryIcon className="nav-options__icon" />
            </div>
          </div>
        </NavMenuOption>
      </li>
      )}
      {appConfig.removeWorkspaces ? null : (
        <li
          key="nav_link-workspaces"
          className={classNames('nav-options__link', {
            'nav-options__link--selected':
              selectedComponent &&
              selectedComponent.type === (<NavWorkspacePanel />).type,
          })}
          onClick={() => props.onClick(<NavWorkspacePanel />)}
        >
          <NavMenuOption
            navLabel={intl.formatMessage({ id: 'navOptions.workspaceHeader' })}
          >
            <div>
              <div>
                <WorkspaceIcon className="nav-options__icon" />
              </div>
            </div>
          </NavMenuOption>
        </li>
      )}
      {appConfig.menuOptions &&
      appConfig.menuOptions.formsMenu &&
      appConfig.menuOptions.formsMenu.enabled ? (
        <li
          key="nav_link-forms"
          className={classNames('nav-options__link', {
            'nav-options__link--selected':
              selectedComponent &&
              selectedComponent.type === (<NavFormsPanel />).type,
          })}
          onClick={() => props.onClick(<NavFormsPanel />)}
        >
          <NavMenuOption
            navLabel={intl.formatMessage({ id: 'navOptions.formsHeader' })}
          >
            <div>
              <div>
                <FormIcon className="nav-options__icon" />
              </div>
            </div>
          </NavMenuOption>
        </li>
      ) : null}
      {appConfig.useHighlights ? (
        <li
          key="nav_link-highlights"
          className={classNames('nav-options__link', {
            'nav-options__link--selected':
              selectedComponent &&
              selectedComponent.type === (<NavSetsPanel />).type,
          })}
          onClick={() => props.onClick(<NavSetsPanel />)}
        >
          <NavMenuOption
            navLabel={intl.formatMessage({ id: 'navOptions.setsHeader' })}
          >
            <div>
              <div>
                <HighlightIcon className="nav-options__icon" />
              </div>
            </div>
          </NavMenuOption>
        </li>
      ) : null}
      {appConfig.thesaurus && appConfig.thesaurus.show ? (
        <li
          key="nav_link-thesaurus"
          className={classNames('nav-options__link', {
            'nav-options__link--selected':
              selectedComponent &&
              selectedComponent.type === (<NavThesaurusPanel />).type,
          })}
          onClick={() => props.onClick(<NavThesaurusPanel />)}
        >
          <NavMenuOption
            navLabel={
              appConfig.thesaurus.mainLabel
                ? appConfig.thesaurus.mainLabel
                : intl.formatMessage({ id: 'navOptions.thesaurusHeader' })
            }
          >
            <div>
              <div>
                <ShareDefaultIcon className="nav-options__icon" />
              </div>
            </div>
          </NavMenuOption>
        </li>
      ) : null}
      {appConfig?.menuOptions?.reportsMenu?.length > 0 && (
        <li
          key="nav_link-record"
          className={classNames('nav-options__link', {
            'nav-options__link--selected':
              selectedComponent &&
              selectedComponent.type === (<NavReportsPanel />).type,
          })}
          onClick={() => props.onClick(<NavReportsPanel />)}
        >
          <NavMenuOption
            navLabel={intl.formatMessage({ id: 'navOptions.reportsHeader' })}
          >
            <div>
              <div>
                <DocDefault
                  className="nav-options__icon"
                  width="28"
                  height="28"
                />
              </div>
            </div>
          </NavMenuOption>
        </li>
      )}
      {externalNavLinks}
    </ul>
  );
}

NavOptions.propTypes = {
  selectedComponent: PropTypes.element,
  onClick: PropTypes.func,
  onHomeClick: PropTypes.func,
  isNavExpanded: PropTypes.bool,
  onExternalLinkClick: PropTypes.func,
};

export default injectIntl(NavOptions);
